// resetLiveStream.js
'use strict';
import {
  SET_LIVE_STREAM_DATA,
  MERGE_LIVE_STREAM_DATA,
  REMOVE_OPERATION_DATA,
  SET_LIST_ITEMS,
  MERGE_OPERATION_DATA,
} from '../ActionTypes.js';
import updateLiveStreamPreset from '../action/updateLiveStreamPreset.js';
import setLiveStreamPanel from '../action/setLiveStreamPanel.js';
import removeModals from '../action/removeModals.js';
import clearLivestreamPaymentTimer from '../action/clearLivestreamPaymentTimer.js';
import clearRemoveStreamTokenTimer from '../action/clearRemoveStreamTokenTimer.js';
import clearLivestreamStatusTimer from '../action/clearLivestreamStatusTimer.js';
import clearLivestreamCounterTimer from '../action/clearLivestreamCounterTimer.js';
import setIsPrivateStreamUnlocked from '../action/setIsPrivateStreamUnlocked.js';
import { submitEmptyStreamRatingThenClose } from '../action/livestreamRatingHintActions.js';
import { clearModalQueue } from '../action/modalQueueActions.js';
import getModalData from '../selector/getModalData.js';
import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import {
  liveStreamSessionLeaderboardCategory,
  livestreamNoticeType,
  PRESET_SD,
  PRESET_PREVIEW,
} from '../resource/liveStreamConstants.js';
import { karaokeType } from '../resource/goalConstants.js';

/**
 * Reset live stream
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {boolean} {isLeaveLivestream} - is leave livestream. If isLeaveLivestream is true, it means the user leaves the livestream and does not need to reset the private show timer.
 * Note: Because this resetLiveStream is also called when the sessionId changes, when the sessionId changes, all current data should be cleared, instead of keeping timers as when leaving the livestream.
 * @return {Promise} Action promise.
 */
const resetLiveStream =
  ({ streamId, isLeaveLivestream }) =>
  async (dispatch, getState) => {
    if (!streamId) return { type: '' };
    const sessionId = getLiveStreamData(getState(), [streamId], 'sessionId');
    const streamToken = getOperationData(
      getState(),
      ['liveStream', 'paid', streamId],
      'streamToken'
    );
    dispatch(
      updateLiveStreamPreset({
        streamId,
        preset: streamToken ? PRESET_SD : PRESET_PREVIEW,
        pusherPreset: PRESET_SD,
      })
    );
    dispatch({
      type: SET_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'pinned'],
        data: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, 'chat'],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: [
          'liveStream',
          streamId,
          livestreamNoticeType.USER_ENTERED_NOTICE,
        ],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, livestreamNoticeType.GIFT_NOTICE],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: [
          'liveStream',
          streamId,
          livestreamNoticeType.TICKET_NOTICE,
        ],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: [
          'liveStream',
          streamId,
          'session',
          sessionId,
          'leaderboard',
          liveStreamSessionLeaderboardCategory.TOTAL,
        ],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, 'goal', karaokeType.DEFAULT],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIST_ITEMS,
      payload: {
        selectPath: ['liveStream', streamId, 'goal', karaokeType.TOY_DEVICE],
        itemIds: [],
      },
    });
    dispatch({
      type: SET_LIVE_STREAM_DATA,
      payload: {
        selectPath: [
          streamId,
          'session',
          sessionId,
          'leaderboard',
          liveStreamSessionLeaderboardCategory.TOTAL,
        ],
        data: {},
      },
    });
    dispatch({
      type: SET_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'goal'],
        data: {},
      },
    });
    dispatch({
      type: SET_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'diamondCount'],
        data: {},
      },
    });
    dispatch({
      type: SET_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'chat'],
        data: {},
      },
    });
    const previewDelaySubscribeTimerId = getOperationData(
      getState(),
      ['timer', 'livestream', streamId, PRESET_PREVIEW],
      'delaySubscribe'
    );
    if (previewDelaySubscribeTimerId) {
      clearTimeout(previewDelaySubscribeTimerId);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['timer', 'livestream', streamId, PRESET_PREVIEW],
          data: {
            delaySubscribe: null,
          },
        },
      });
    }
    const sdDelaySubscribeTimerId = getOperationData(
      getState(),
      ['timer', 'livestream', streamId, PRESET_SD],
      'delaySubscribe'
    );
    if (sdDelaySubscribeTimerId) {
      clearTimeout(sdDelaySubscribeTimerId);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['timer', 'livestream', streamId, PRESET_SD],
          data: {
            delaySubscribe: null,
          },
        },
      });
    }

    const activeStreamId = getOperationData(
      getState(),
      ['liveStream'],
      'activeStreamId'
    );
    const isActiveStream = activeStreamId === streamId;

    const privateShowPopupTimerId = getOperationData(
      getState(),
      ['timer', 'modal'],
      'LiveStreamPrivateShowPopup'
    );

    if (privateShowPopupTimerId && isActiveStream) {
      clearTimeout(privateShowPopupTimerId);
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['timer', 'modal'],
          data: {
            LiveStreamPrivateShowPopup: null,
          },
        },
      });
    }

    if (isLeaveLivestream && isActiveStream) {
      dispatch(
        setLiveStreamPanel({
          isChatPanelOpen: false,
          isGiftPanelOpen: false,
          isGiftPanelSwiping: false,
          isGiftTooltipVisible: false,
        })
      );
    }
    if (!isLeaveLivestream) {
      dispatch(
        setIsPrivateStreamUnlocked({ streamId, isPrivateStreamUnlocked: false })
      );
      dispatch(clearRemoveStreamTokenTimer({ streamId }));
      dispatch({
        type: REMOVE_OPERATION_DATA,
        payload: {
          selectPath: ['liveStream', 'paid', streamId],
        },
      });
      dispatch({
        type: MERGE_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId],
          data: {
            shop: undefined,
            pricing: undefined,
            settings: undefined,
          },
        },
      });
      const privateShowTimerId = getOperationData(
        getState(),
        ['timer', 'livestream', streamId],
        'privateShow'
      );
      if (privateShowTimerId) {
        clearTimeout(privateShowTimerId);
        dispatch({
          type: MERGE_OPERATION_DATA,
          payload: {
            selectPath: ['timer', 'livestream', streamId],
            data: {
              privateShow: null,
            },
          },
        });
      }
    }

    if (activeStreamId === streamId) {
      dispatch(
        removeModals({
          ids: ['PrivateShowBuyTicketModal', 'LiveStreamPrivateShowPopup'],
          transitionStatus: 'closing',
        })
      );
    }

    dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'presets'],
        data: {
          [PRESET_PREVIEW]: {
            counter: null,
          },
          [PRESET_SD]: {
            counter: null,
          },
        },
      },
    });
    dispatch(clearLivestreamStatusTimer({ streamId }));
    dispatch(clearLivestreamCounterTimer({ streamId }));
    dispatch(clearLivestreamPaymentTimer({ streamId }));
    dispatch(
      setIsPrivateStreamUnlocked({ streamId, isPrivateStreamUnlocked: false })
    );

    dispatch(
      removeModals({
        ids: [
          // group private modals
          'ModalTriggerPrivateSucceeded',
          'GroupPrivateDeclinedModal',
          // exclusive private modals
          'ModalTriggerExclusiveSucceeded',
          'ModalTriggerExclusiveFailed',
          'ModalTurnToExclusivePrivate',
          // mini profile modals
          'MiniProfileModal',
          'MiniBadgeInfoModal',
        ],
        transitionStatus: 'closing',
      })
    );

    const isRatingOpened = ['LivestreamRatingHint', 'LivestreamRatingModal']
      .map(id => getModalData(getState(), id, 'status'))
      .some(status => ['display', 'opening'].includes(status));

    if (isRatingOpened) {
      dispatch(submitEmptyStreamRatingThenClose({ streamId }));
    }

    dispatch(clearModalQueue({ modalId: 'LivestreamCommandProgressModal' }));
  };

export default resetLiveStream;
